import { styled } from '@linaria/react';
import { Group, Text } from '@mantine/core';
import { formatDateRange } from '../../../../helpers/date-time-helpers/formatting/formatting';
import { Icon } from '../../../../ui-library/icon/icon';
import type { FloraContextualInfo } from '../../flora.type';
import type { MentionItem } from '../flora-mention/flora-mention.type';
import { MentionPill } from '../flora-mention/mention-pill';
import { mapObjectTypeToMentionType } from './flora-context-banner.helpers';

interface FloraContextBannerProps {
  contextualInfo?: FloraContextualInfo | null;
  /**
   * Human-readable label for a non-portfolio surface (e.g. the published page
   * the user is viewing) that has no place in `FloraContextualInfo`'s
   * object model. Rendered as its own chip so the page-viewer dock can show
   * context in the same bar as everywhere else.
   */
  pageTitle?: string;
  isExpanded: boolean;
  onToggle: () => void;
}

/**
 * Collapsible bar summarizing the context Flora is grounded in — the current
 * objects, date range, and selected measures from `contextualInfo`, plus an
 * optional `pageTitle` chip for surfaces (like the published-page dock) that
 * have no place in the object model. Renders nothing when there is no
 * contextual object, date range, selected measure, or page title.
 *
 * @param {FloraContextualInfo | null} [contextualInfo] - Portfolio-derived context; may be null/undefined
 * @param {string} [pageTitle] - Label for a non-portfolio surface, shown as its own chip
 * @param {boolean} isExpanded - Whether the banner body is expanded
 * @param {() => void} onToggle - Toggles the expanded state
 * @returns {JSX.Element | null} The context banner, or null when there is nothing to show
 */
export const FloraContextBanner = ({ contextualInfo, pageTitle, isExpanded, onToggle }: FloraContextBannerProps) => {
  const currentObjects = contextualInfo?.currentObjects ?? [];
  const startDate = contextualInfo?.startDate ?? null;
  const endDate = contextualInfo?.endDate ?? null;
  const selectedMeasures = contextualInfo?.selectedMeasures;

  const hasContext =
    currentObjects.length > 0 || (startDate && endDate) || (selectedMeasures?.length ?? 0) > 0 || Boolean(pageTitle);
  if (!hasContext) {
    return null;
  }

  const dateRange = startDate && endDate ? formatDateRange(startDate, endDate) : null;

  return (
    <BannerContainer>
      <BannerHeader onClick={onToggle}>
        <Text size="xs" fw={500} c="var(--ink-lighter)">
          Context
        </Text>
        <Icon name={isExpanded ? 'expand_more' : 'expand_less'} size={14} color="var(--ink-lighter)" />
      </BannerHeader>

      {isExpanded && (
        <PillsRow>
          <Group gap={4} wrap="wrap">
            {pageTitle && (
              <PageChip>
                <Icon name="description" size={12} color="var(--ink-lighter)" />
                <Text size="xs" c="var(--ink-light)">
                  {pageTitle}
                </Text>
              </PageChip>
            )}
            {currentObjects
              .map((obj) => {
                const mentionType = mapObjectTypeToMentionType(obj.type);
                if (!mentionType) {
                  return null;
                }
                const mentionItem: MentionItem = {
                  id: obj.id,
                  name: obj.name,
                  type: mentionType,
                };
                return <MentionPill key={`${obj.type}-${obj.id}`} mention={mentionItem} readonly />;
              })
              .filter(Boolean)}
            {dateRange && (
              <DateRangeChip>
                <Icon name="calendar_month" size={12} color="var(--ink-lighter)" />
                <Text size="xs" c="var(--ink-light)">
                  {dateRange}
                </Text>
              </DateRangeChip>
            )}
            {selectedMeasures && selectedMeasures.length > 0 && (
              <MeasuresChip>
                <Icon name="show_chart" size={12} color="var(--ink-lighter)" />
                <Text size="xs" c="var(--ink-light)">
                  {selectedMeasures.join(', ')}
                </Text>
              </MeasuresChip>
            )}
          </Group>
        </PillsRow>
      )}
    </BannerContainer>
  );
};

const BannerContainer = styled.div`
  padding: 6px 12px 8px;
  background: var(--sky-lightest);
  border-top: 1px solid var(--sky-light);
  border-bottom: 1px solid var(--sky-light);
`;

const BannerHeader = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  background: none;
  border: none;
  padding: 2px 6px;
  cursor: pointer;
  border-radius: var(--border-radius-small);

  &:hover {
    background: var(--sky-light);
  }
`;

const PillsRow = styled.div`
  padding-top: 6px;
`;

const DateRangeChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: var(--border-radius-small);
  border: 1px solid var(--sky-light);
  background: var(--base-white);
`;

const MeasuresChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: var(--border-radius-small);
  border: 1px solid var(--sky-light);
  background: var(--base-white);
`;

const PageChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: var(--border-radius-small);
  border: 1px solid var(--sky-light);
  background: var(--base-white);
`;
